import {
  MAX_PARALLEL_BLOB_SENDS,
  BLOB_SEND_QUEUE_MIN_BYTES,
} from "@/config/constants";
import { createFifoSemaphore } from "@/lib/fifoSemaphore";
import { holdBlobSendLiveness } from "@/lib/uploadSlots";

/**
 * Global cap on concurrent single-blob attachment SENDS, per tab — documents,
 * images, voice, edit-with-attachment, and forward. Each of these encrypts the
 * whole file in one shot, so a send transiently costs ~3x its size in the heap;
 * documents and images can be ~100 MB, so a burst of large sends OOMs the tab —
 * the stuck-PENDING failure this queue exists to prevent.
 *
 * QUEUE, don't reject: the user already committed to sending, so making them wait
 * beats a toast. Wrap ONLY the encrypt+upload span with `runBlobSend` (after the
 * optimistic write, so the bubble appears immediately and stays ordered).
 *
 * Distinct from the chunked-video cap (MAX_PARALLEL_CHUNKED_UPLOADS), which gates
 * a different path with a reject+toast and a cross-tab Web-Locks/429 backstop.
 * This one is a per-tab in-memory FIFO, sibling of the receive-side
 * autoDownloadQueue.
 */
const queue = createFifoSemaphore(MAX_PARALLEL_BLOB_SENDS);

/**
 * Run the encrypt+upload span under the send cap; the slot is released on throw.
 *
 * The queue is bypassed (runs immediately, holds no cap slot) when either:
 *  - `skip` — the new-AI-chat first send, which sits behind a full-screen
 *    "creating conversation" loader where queueing would freeze the user, and
 *    has nothing to overlap with anyway; or
 *  - `sizeBytes` is below BLOB_SEND_QUEUE_MIN_BYTES — a small send (voice note,
 *    small image) costs little heap, so the cap (which exists to bound the OOM
 *    peak of big encrypts) would only add latency. `sizeBytes` omitted → treated
 *    as large and queued, so the memory guard never regresses on unknown sizes.
 *
 * Either way the span holds a cross-tab liveness beacon (`holdBlobSendLiveness`)
 * for its duration so a cold-booting tab's stranded-send reconciliation can tell
 * this LIVE send apart from a dead one and not wrongly FAIL it. The beacon covers
 * the long encrypt+upload span; a narrow window remains between the span ending
 * and the message actually posting (see strandedSendReconciliation).
 */
export function runBlobSend<T>(
  task: () => Promise<T>,
  { skip = false, sizeBytes }: { skip?: boolean; sizeBytes?: number } = {},
): Promise<T> {
  const bypassQueue =
    skip || (sizeBytes != null && sizeBytes < BLOB_SEND_QUEUE_MIN_BYTES);
  const withLiveness = async (): Promise<T> => {
    const release = await holdBlobSendLiveness();
    try {
      return await task();
    } finally {
      release();
    }
  };
  return bypassQueue ? withLiveness() : queue.run(withLiveness);
}

/**
 * Per-tab count of in-flight blob sends. Used by the stranded-send
 * reconciliation as the fallback liveness signal where cross-tab Web Locks
 * (`heldBlobSendCount`) is unavailable.
 */
export const blobSendSessionCount = queue.active;

/** Test-only: reset the shared counters between cases. */
export const __resetBlobSendQueue = queue.reset;

/** Test-only: current in-flight send count. */
export const __blobSendActive = queue.active;
